import { DomainId } from '../../../utility/claim-id';
import { terms } from '../../../language/terms';

/**
 * The binding from a security domain code to the words that name it on screen.
 *
 * THIS FILE HOLDS NO WORDS. Every string below is composed from `terms`, so AGWA renaming an
 * operation type is one edit in the glossary and none here. It held them until 2026-08-29, and
 * D-068 records what that cost: following ONE rename from "Glass Processor" to "Prime Glass
 * Manufacturer" meant editing the seeded displayName, the column it seeds, the label AND the
 * description here, 22 lines across four acceptance documents, and 19 journey files.
 *
 * A binding cannot be dispensed with even though the words have gone. `defineTerms` preserves each
 * key so `terms.loginGroup` is checked by the compiler rather than looked up by string, while
 * domain codes arrive as runtime data from the server - something has to join the two, and this is
 * it. What it must never again do is hold a second spelling of a word a term already owns.
 *
 * KEYED ON THE CODE ALONE. It was keyed on codes AND display names, because `JobGetGroups`
 * projected a site's types as display names while its two sibling jobs projected codes. That was a
 * contract defect, fixed at the source on 2026-08-29: a DTO carries a stable code and the client
 * resolves the word. The function written to bridge the two vocabularies is gone with it.
 */

/**
 * Composed once at module load, matching how `terms` itself is built.
 *
 * Eager rather than lazy is deliberate and follows `term-debug.ts`: every form of every term is
 * derived once at import time, so a value computed later could not differ from one computed now.
 * A lazy binding would imply a liveness the glossary does not have.
 *
 * Keys are lower-cased on the way in and every lookup lower-cases too, following the repository's
 * standing rule on comparing enum-like strings that arrive from an API response.
 */
function bind(entries: Record<string, string>): ReadonlyMap<string, string> {
  return new Map(
    Object.entries(entries).map(([code, label]) => [code.toLowerCase(), label])
  );
}

/**
 * Every domain the catalogue seeds, and the words that name it.
 *
 * The ten OPERATION TYPES each resolve to a single term. The four ACCESS GRANTS are COMPOSED from
 * two - a subject and a relationship - so that the subject word stays owned by its own term. That
 * composition is the point rather than a convenience: authoring "Glass Processor Agent" as one
 * string would put the retired word back on a screen beside the type now labelled "Prime Glass
 * Manufacturer", which is the two-names-for-one-thing defect this collapse exists to remove.
 *
 * AGWA's own tenant marker is the fifteenth entry and is neither: it names one organisation and
 * composes nothing.
 */
const domainLabels = bind({
  // ── Operation types: what happens at a site ────────────────────────────────
  [DomainId.primeSystemSupplier]: terms.primeSystemSupplier.value,
  [DomainId.primeSystemIntellectualProperty]:
    terms.primeSystemIntellectualProperty.value,
  [DomainId.primeSystemManufacturer]: terms.primeSystemManufacturer.value,
  [DomainId.glassProcessor]: terms.primeGlassManufacturer.value,
  [DomainId.primeGlassSupplier]: terms.primeGlassSupplier.value,
  [DomainId.fabricator]: terms.fabricator.value,
  [DomainId.reseller]: terms.reseller.value,
  [DomainId.installer]: terms.installer.value,
  [DomainId.glassWholesaler]: terms.glassWholesaler.value,
  [DomainId.glazier]: terms.glazier.value,

  // ── Access grants: authority over ANOTHER member's records ─────────────────
  // An Agent acts on behalf of; an Auditor has authority over. The distinction is carried by the
  // terms themselves, which is where its explanation now lives.
  [DomainId.glassDataEntry]: `${terms.primeGlassManufacturer.value} ${terms.agent.value}`,
  [DomainId.glassAuditor]: `${terms.glassAccreditation.value} ${terms.auditor.value}`,
  [DomainId.productDataEntry]: `${terms.productRegistry.value} ${terms.agent.value}`,
  [DomainId.productAuditor]: `${terms.productRegistry.value} ${terms.auditor.value}`,

  // AGWA's own tenant marker, internal and never offered for assignment.
  [DomainId.systemTenant]: terms.agwa.value,
});

/**
 * What granting each operation type actually does, shown beside the button that grants it.
 *
 * SIX OF THE TEN SAY SO EXPLICITLY: they classify a site and grant nothing. They exist for the
 * Audit Module (D-058) and have no work area behind them yet. Writing them in the same "gives this
 * site its own work area" voice as the one that DOES grant something would be a facade - a person
 * reading it would expect a capability that does not exist.
 *
 * THE SUPPLIER AND THE MANUFACTURER ARE A THIRD CASE since [D-127], and their descriptions have to
 * say both halves of it: they classify the site and give it nothing, AND registering either is
 * what gives the COMPANY its Prime System IP register. Saying only the first would contradict the
 * confirmation the same tick raises a moment later; saying only the second would repeat the
 * site-work-area facade in new words.
 *
 * THE TENTH HAS NO BUTTON TO SIT BESIDE. Prime System IP is never offered on a site's Overview
 * (D-127) - the site that carries it is created by the system - so its entry says what the type IS
 * rather than what registering it would do. It is written because a company row or a staff chip can
 * wear the type and be asked to explain itself, and because every seeded domain but the tenant
 * marker is required to carry one.
 *
 * The definitions are AGWA's own words from `security-catalog.yml`, so the meaning keeps one
 * source; only the domain nouns inside them are interpolated.
 */
const CLASSIFICATION_ONLY = `Recorded as a classification only: it grants no work area yet.`;

const domainDescriptions = bind({
  // ── The one that grants this site a work area ──────────────────────────────
  [DomainId.glassProcessor]: `Gives this ${terms.loginSite.lower} its own ${terms.glassAccreditation.value} work area to create and maintain its own glass certificate records.`,

  // ── The two that give the company its register ─────────────────────────────
  [DomainId.primeSystemSupplier]: `Owns or controls system ${terms.product.lower} data and supplies the ${terms.product.lower} system or intellectual property. Registering it gives the ${terms.loginGroup.lower} its ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower}; the ${terms.loginSite.lower} itself gains nothing.`,
  [DomainId.primeSystemManufacturer]: `Manufactures finished ${terms.product.lowerPlural} at a ${terms.loginSite.lower} using ${terms.loginGroup.lower}-owned ${terms.primeSystemIntellectualProperty.value}. Registering it gives the ${terms.loginGroup.lower} its ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower}; the ${terms.loginSite.lower} itself gains nothing.`,
  // Bound although no control offers it: the catalogue marks it uiAssignable: false, and
  // `getOrganizationTypeLabel` throws on an unbound code, so a company row or staff chip wearing
  // this type needs the binding whether or not anything grants it. AGWA's own words from
  // security-catalog.yml, with the domain nouns interpolated like every description here.
  [DomainId.primeSystemIntellectualProperty]: `The ${terms.product.lower} system IP a ${terms.loginGroup.lower} owns. The module owner of the ${terms.productRegistry.value}; ${terms.primeSystemSupplier.value} and ${terms.primeSystemManufacturer.value} are the ${terms.loginSite.lower} classifications that consume it.`,

  // ── The six that classify and grant nothing yet ────────────────────────────
  [DomainId.primeGlassSupplier]: `Supplies foundational glass material and associated source performance data. ${CLASSIFICATION_ONLY}`,
  [DomainId.fabricator]: `Manufactures finished ${terms.product.lowerPlural} using systems supplied or authorised by a ${terms.primeSystemSupplier.value} or ${terms.primeSystemManufacturer.value}; does not own the prime ${terms.product.lower} IP. ${CLASSIFICATION_ONLY}`,
  [DomainId.reseller]: `Purchases and resells finished ${terms.product.lowerPlural}; does not own the underlying ${terms.product.lower} IP. ${CLASSIFICATION_ONLY}`,
  [DomainId.installer]: `Installs finished ${terms.product.lowerPlural} on site; service behaviour only. ${CLASSIFICATION_ONLY}`,
  [DomainId.glassWholesaler]: `Wholesales or resells bulk or processed glass. ${CLASSIFICATION_ONLY}`,
  [DomainId.glazier]: `Installs glass and glazing systems; service behaviour only. ${CLASSIFICATION_ONLY}`,

  // ── The delegation grants ──────────────────────────────────────────────────
  [DomainId.productDataEntry]: `Lets this ${terms.loginGroup.lower} perform ${terms.productRegistry.value} data entry on behalf of other ${terms.loginGroup.lowerPlural}.`,
  [DomainId.productAuditor]: `Lets this ${terms.loginGroup.lower} audit and verify other ${terms.loginGroup.lowerPluralPossessive} ${terms.productRegistry.value} records.`,
  [DomainId.glassDataEntry]: `Lets this ${terms.loginGroup.lower} perform ${terms.glassAccreditation.value} data entry on behalf of other ${terms.loginGroup.lower} ${terms.loginSite.lowerPlural}.`,
  [DomainId.glassAuditor]: `Lets this ${terms.loginGroup.lower} audit and verify other ${terms.loginGroup.lower} ${terms.loginSite.lowerPluralPossessive} ${terms.glassAccreditation.value} records.`,
});

/**
 * Shown under a delegation access grant whose button is disabled, explaining why it is not offered
 * as an ordinary self-service option.
 */
export const delegationDomainNote = `Only required to delegate ${terms.agwa.value} abilities to ${terms.loginGroup.lowerPlural} who are ${terms.agwa.value} work contractors, not actual ${terms.member.lowerPlural}.`;

/**
 * Resolve a domain code to the words users see.
 *
 * THROWS when the code has no binding, deliberately, and the throw survived the collapse for a
 * reason D-068 states: an unresolved TERM renders a `MISSING TERM` marker and never throws, because
 * a missing word is a vocabulary gap. A domain code with no binding is a different failure - the
 * server is issuing something this build has never heard of - and flattening the two would hide it.
 *
 * That is not hypothetical. It is exactly what happened here: D-067 renamed the registry domain,
 * the UI's code list was never followed through, and eight member screens threw on codes the server
 * was already sending. The guard that would have caught it now reads the catalogue itself - see
 * `organization-type-labels.node.spec.ts`, which asserts both directions rather than iterating a
 * hand-maintained UI list that can go stale in the same way.
 */
export function getOrganizationTypeLabel(domainCode?: string | null): string {
  const label = domainLabels.get((domainCode ?? '').trim().toLowerCase());
  if (label) return label;

  throw new Error(
    `${terms.userGroupDomain.value} "${domainCode?.trim() || '(empty)'}" has no binding in organization-type-labels.ts. ` +
      `Bind it to a term rather than letting the raw domain code reach the screen. ` +
      `If this type is new it also needs a term, a description, and a tier-1 acceptance scenario.`
  );
}

/**
 * What granting this domain does, or null where nothing is written for it.
 *
 * Null rather than a throw, unlike the label: a description is supplementary copy beside a control,
 * and a screen must not go down because one is absent. The label is the part a person reads as the
 * identity of the thing, so that is where an unknown code has to fail loudly.
 */
export function getOrganizationTypeDescription(
  domainCode?: string | null
): string | null {
  return (
    domainDescriptions.get((domainCode ?? '').trim().toLowerCase()) ?? null
  );
}

/**
 * Whether registering this type gives THIS SITE a work area of its own.
 *
 * ONE TYPE DOES: Prime Glass Manufacturer, whose certificate records are the site's. It answered
 * true for Prime System Supplier as well until [D-127], and the narrowing is the whole of what
 * that decision changed here - the supplier no longer owns anything. What a supplier or
 * manufacturer registration gives is the COMPANY's register, which is a different promise made to
 * a different party, and `grantsCompanyRegister` below is where it is asked. Prime System IP is
 * registrable by nobody, so no dialog asks either question about it.
 *
 * WHY THE TWO ARE SEPARATE PREDICATES rather than one renamed to cover both: the site work area
 * and the company register differ in who gains access. Registering a Prime Glass Manufacturer
 * gives everyone assigned to that site its work area; registering a supplier gives the company a
 * register nobody may edit until they are named on the staff page (D-127 rule 6). Collapsing them
 * would force one sentence to describe both, and that sentence would be false about access for one
 * of them - which is exactly the facade this predicate exists to prevent. Telling someone that
 * registering as a Fabricator will "create a work area" promises a capability that does not exist,
 * and telling someone that deactivating a Prime Glass Manufacturer merely "changes a
 * classification" hides that a work area and everything in it is about to vanish for every person
 * assigned to that site.
 */
export function grantsWorkArea(domainCode?: string | null): boolean {
  const code = (domainCode ?? '').trim().toLowerCase();
  return code === DomainId.glassProcessor.toLowerCase();
}

/**
 * Whether registering this type gives the COMPANY its Prime System IP register.
 *
 * The supplier and the manufacturer both do, and neither does anything else ([D-127]): they are
 * classifications that consume one register, created the first time either is registered anywhere
 * in the company and kept alive while any active site holds either. That is why both answer the
 * same - a site that is only a manufacturer still gives its company the register, which is what
 * `@PIP-LIFE-002` asserts.
 *
 * Read by the site's Overview so its confirmations can say what the registration actually does.
 * Nothing about the SITE changes: it gains no work area, and being assigned to it grants nobody
 * the right to edit the register (`@PIP-EDIT-003`).
 */
export function grantsCompanyRegister(domainCode?: string | null): boolean {
  const code = (domainCode ?? '').trim().toLowerCase();
  return (
    code === DomainId.primeSystemSupplier.toLowerCase() ||
    code === DomainId.primeSystemManufacturer.toLowerCase()
  );
}

/**
 * Map a list of domain codes to their labels.
 *
 * No filtering of empties: `getOrganizationTypeLabel` either returns a label or throws, so a
 * silently dropped entry is not possible. A list rendering fewer chips than the data holds was the
 * other way an unmapped code could mislead.
 */
export function mapOrganizationTypeLabels(
  values: Array<string | null | undefined>
): string[] {
  return values.map((value) => getOrganizationTypeLabel(value));
}

/**
 * The one operation type another type already implies, so only the implying one is shown.
 *
 * A Prime System Manufacturer makes the system it supplies, so anything that is both is described
 * by the manufacturer alone. Showing both says nothing the first badge did not, and two badges for
 * one capability reads as two separate businesses. Architect's ruling, 2026-08-30, and widened on
 * 2026-09-17: "PSS always collapses to PSM, anywhere it is shown". Until then a company row
 * collapsed and a site row did not, on the reasoning that on one site the two are a real
 * distinction; the architect retired that distinction, so every badge, chip and tag now reads
 * through this one rule and no screen can disagree with another about what a pair means.
 *
 * Expressed as a map from the implied type to the type that implies it, rather than as an if. The
 * next pair AGWA names is then a line of data here instead of another branch, and the reason a code
 * is missing from a badge stays readable in one place.
 */
const IMPLIED_BY_BROADER_TYPE: ReadonlyMap<string, string> = new Map([
  [DomainId.primeSystemSupplier, DomainId.primeSystemManufacturer],
]);

/**
 * Filters a list of operation-type codes down to what a badge should say.
 *
 * DISPLAY ONLY. The underlying codes stay whole wherever they are filtered on, because a type
 * filter reads the same field: collapsing at the data level would stop a filter on Prime System
 * Supplier matching a company, site or person that genuinely is one, which is a different and
 * wrong change. Matching is case-insensitive, as every enum-like string from the API is compared.
 */
export function collapseOperationTypeCodes(codes: readonly string[]): string[] {
  const held = new Set(codes.map((code) => code.toLowerCase()));
  return codes.filter((code) => {
    const broader = IMPLIED_BY_BROADER_TYPE.get(
      [...IMPLIED_BY_BROADER_TYPE.keys()].find(
        (key) => key.toLowerCase() === code.toLowerCase()
      ) ?? ''
    );
    return broader === undefined || !held.has(broader.toLowerCase());
  });
}
